import { Shuffle } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

/**
 * DCO (Dynamic Creative Optimization) campaigns follow a different flow than
 * standard campaigns, so they need to be visually unmistakable everywhere they
 * appear. The badge intentionally breaks from the pastel status-pill palette:
 * a saturated violet→fuchsia gradient reads as "special mode", not "status".
 *
 * `dco_status` comes from MAP and is read-only: "DCO Enabled", "No DCO", or
 * null (blank in MAP). Only "DCO Enabled" gets the badge.
 */
export function isDcoEnabled(dcoStatus: string | null | undefined): boolean {
  return dcoStatus === 'DCO Enabled';
}

const TOOLTIP_TEXT =
  'Dynamic Creative Optimization — this campaign follows the DCO flow. Set in MAP, read-only here.';

export function DcoBadge({
  compact = false,
  iconOnly = false,
  label = 'DCO',
  tooltip = TOOLTIP_TEXT,
}: {
  compact?: boolean;
  /** Icon-only round chip — minimal footprint for dense table cells */
  iconOnly?: boolean;
  /** Override the pill text, e.g. "Includes DCO" on plan-level badges */
  label?: string;
  tooltip?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {iconOnly ? (
          <span
            className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-500 text-white shadow-sm cursor-default select-none shrink-0"
            aria-label="DCO campaign"
          >
            <Shuffle className="w-2.5 h-2.5" />
          </span>
        ) : (
          <span
            className={`inline-flex items-center rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 font-semibold text-white shadow-sm cursor-default select-none ${
              compact
                ? 'gap-0.5 px-1.5 py-px text-[10px] tracking-wide'
                : 'gap-1 px-2.5 py-0.5 text-xs'
            }`}
          >
            <Shuffle className={compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
            {label}
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-56">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
